import { useRouter } from 'next/router';
import { IPageModel, IReturnTypeOFTargetMboxParam } from '../types';
import { DataLayerEvent, useSession } from '../lib/session';
import { useEffect, useState } from 'react';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import { useClientEnvVarsStore, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { targetPayloadRequest } from '../lib/dynamicContentAssemblyUtils/targetSDKPayload';
import { fetchDCADynamicModelForAlloy } from './useDCADynamicModel';
import { getMboxParamters } from '../lib/helpers';
import { IAlloyCallResponse, Proposition } from '../abTestAlloyResponseType';

export const chaseCodes = ['updateboundless', 'updatebold', 'updatecompboundless', 'updatecompbold'];
export const amexCodes = [
  'updatebrilliant',
  'updatebusiness',
  'updatebevy',
  'updatecompbrilliant',
  'updatecompbusiness',
  'updatecompbevy',
];

export const useDCAAlloyModel = (targetMboxes: IReturnTypeOFTargetMboxParam[]) => {
  const router = useRouter();
  const queryParams = router?.query;
  const DST_VALUES = [...amexCodes, ...chaseCodes];
  const isDSTvalue = DST_VALUES.some(key =>
    Object.keys(queryParams).some(param => param.toLowerCase() === key?.toLowerCase())
  );
  const { datalayer, mvpOffers, updateDatalayer } = useSession();
  const [isCallTriggered, setIsCallTriggered] = useState(false);
  const updatePageModel = useDCAPageModelStore(state => state.setPageModel);

  useEffect(() => {
    if (!isEmpty(datalayer) && !isCallTriggered) {
      setIsCallTriggered(true);
      const listOfTargetMboxes = targetMboxes?.map(item => item.targetMbox);
      if (listOfTargetMboxes?.length > 0 && !isDSTvalue) {
        launchLoadedAddEventListener(listOfTargetMboxes);
      }
    }
  }, [datalayer]);

  const launchLoadedAddEventListener = (listOfTargetMboxes: string[]) => {
    const handleLaunchLoaded = () => {
      fetchTargetData(listOfTargetMboxes)?.then(() => {
        const updatedAlloyComponentWithoutMbox: Record<string, unknown> = {},
          alloyComponentsPageModel: Record<string, unknown> = {};

        targetMboxes.forEach(alloyItem => {
          if (alloyItem.alloyResponse) {
            alloyComponentsPageModel[alloyItem.targetMbox] = { ...alloyItem.alloyResponse };
            const { mboxParameters: list } = getMboxParamters(alloyItem.alloyResponse as unknown as IPageModel);
            if (list.length == 0) {
              updatedAlloyComponentWithoutMbox[alloyItem.targetMbox] = alloyItem.alloyResponse;
            }
          } else if (alloyItem.targetMbox && !alloyItem.alloyResponse) {
            alloyComponentsPageModel[alloyItem.targetMbox] = null;
          }
        });
        if (!isEmpty(updatedAlloyComponentWithoutMbox)) {
          const prevPageModel = useDCAPageModelStore?.getState()?.pageModel as Record<string, Record<string, unknown>>;
          const prevAlloyModelJSON = prevPageModel?.['alloyModelJSON'];

          updatePageModel({
            ...(prevPageModel ? prevPageModel : {}),
            alloyModelJSON: {
              ...prevAlloyModelJSON,
              ...updatedAlloyComponentWithoutMbox,
            },
          });
        }
        const { mboxParameters } = getMboxParamters(alloyComponentsPageModel as unknown as IPageModel);
        fetchDCADynamicModelForAlloy(
          mboxParameters,
          alloyComponentsPageModel as unknown as IPageModel,
          datalayer as Record<string, unknown>,
          mvpOffers
        )?.then(response => {
          const prevPageModel = useDCAPageModelStore?.getState()?.pageModel as Record<string, unknown>;
          const prevAlloyModelJSON = prevPageModel?.['alloyModelJSON'] || {};

          updatePageModel({
            ...(prevPageModel ? prevPageModel : {}),
            alloyModelJSON: {
              ...prevAlloyModelJSON,
              ...(isEmpty(response) ? alloyComponentsPageModel : response),
            },
          });
        });
      });
    };

    if (window !== undefined && window.adobeDataLayer) {
      window?.adobeDataLayer?.addEventListener('launchLoaded', handleLaunchLoaded);
    }

    const timeoutId = setTimeout(() => {
      if (window !== undefined) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
      }
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
      if (window !== undefined && window.adobeDataLayer) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
      }
    };
  };

  const fetchTargetData = async (listOfTargetMboxes?: string[]) => {
    const payLoad = targetPayloadRequest(
      {
        isUxl: 'false',
        locale:
          window?.sessionData?.locale?.replace('-', '_') ||
          window?._satellite?.['getVar']('brwsrLang') ||
          datalayer?.['brwsrLang'] ||
          'en_US',
        mboxName: listOfTargetMboxes,
        propertyToken:
          process.env['TARGET_PROPERTY_TOKEN'] ||
          useClientEnvVarsStore.getState().envVarsObject['TARGET_PROPERTY_TOKEN'],
      },
      datalayer,
      mvpOffers
    );
    try {
      const result = await window?.alloy<IAlloyCallResponse>('sendEvent', {
        data: {
          __adobe: {
            target: payLoad?.execute?.mboxes?.[0]?.parameters,
          },
        },
        xdm: {},
        decisionScopes: listOfTargetMboxes,
      });

      const propositions = result?.propositions;
      let item: string;
      const experience: string[] = [];
      let foundProposition = false;
      const propositionItems: Proposition[] = [];

      if (propositions && targetMboxes?.length) {
        for (const proposition of propositions) {
          item = proposition?.scope;
          propositionItems.push(proposition);
          foundProposition = true;

          if (item) {
            targetMboxes?.forEach(element => {
              if (element.targetMbox === item) {
                element.alloyResponse = proposition?.items?.[0].data.content?.[':items'].root?.[':items']
                  ?.responsivegrid?.[':items']?.cobrandwrapper as unknown as Record<string, unknown>;
                experience?.push(proposition?.items[0].meta['experience.name']);
              }
            });
          }
        }

        if (foundProposition) {
          updateDatalayer({
            event: 'card-layout-updated',
            data: {
              items: propositionItems,
              experiences: experience,
              targetData: targetMboxes?.map(item => ({
                [item.targetMbox]: item.alloyResponse,
              })),
            },
            skipDatalayerUpdate: true,
          } as unknown as DataLayerEvent);
        }
      }

      if (propositionItems.length) {
        window?.alloy('sendEvent', {
          xdm: {
            eventType: 'decisioning.propositionDisplay',
            _experience: {
              decisioning: {
                propositions: propositionItems?.map(item => ({
                  id: item?.id,
                  scope: item?.scope,
                  scopeDetails: item?.scopeDetails,
                })),
                propositionEventType: {
                  display: 1,
                },
              },
            },
          },
        });
      }
    } catch (error) {
      console.log('Error occurred in Target call', error);
    }
  };
};
